import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI, applicationsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Plus, 
  Trash2, 
  ArrowLeft,
  DollarSign,
  Calendar,
  User,
  Briefcase,
  Save,
  Send
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, amount: 0 }
  ]);

  useEffect(() => {
    fetchContractorsAndJobs();
  }, []);

  const fetchContractorsAndJobs = async () => {
    try {
      // Fetch completed jobs to get contractors
      const jobsResponse = await applicationsAPI.getMyApplications();
      const applications = jobsResponse.data.applications || [];
      
      // Get unique contractors from accepted applications
      const acceptedApps = applications.filter(app => app.status === 'accepted');
      const uniqueContractors = [];
      const contractorMap = new Map();
      
      acceptedApps.forEach(app => {
        if (app.job?.contractor_id && !contractorMap.has(app.job.contractor_id)) {
          contractorMap.set(app.job.contractor_id, {
            id: app.job.contractor_id,
            name: app.job.contractor_name || 'Unknown Contractor'
          });
          uniqueContractors.push({
            id: app.job.contractor_id,
            name: app.job.contractor_name || 'Unknown Contractor'
          });
        }
      });
      
      setContractors(uniqueContractors);
      
      // Get jobs for dropdown
      const jobsList = acceptedApps.map(app => ({
        id: app.job?._id,
        title: app.job?.title,
        contractor_id: app.job?.contractor_id
      })).filter(j => j.id && j.title);
      
      setJobs(jobsList);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Recalculate amount
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].amount = parseFloat(updated[index].quantity || 0) * parseFloat(updated[index].unit_price || 0);
    }
    
    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, amount: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (parseFloat(taxRate) / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (sendNow = false) => {
    // Validation
    if (!selectedContractor) {
      toast({ title: 'Error', description: 'Please select a contractor', variant: 'destructive' });
      return;
    }
    
    const validItems = lineItems.filter(item => item.description.trim() && item.amount > 0);
    if (validItems.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one line item', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const selectedJobData = jobs.find(j => j.id === selectedJob);
      
      const invoiceData = {
        contractor_id: selectedContractor,
        job_id: selectedJob || null,
        job_title: selectedJobData?.title || null,
        line_items: validItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0
        })),
        notes: notes || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        tax_rate: parseFloat(taxRate) || 0
      };

      const response = await invoiceAPI.createInvoice(invoiceData);
      
      if (sendNow && response.data.invoice?._id) {
        await invoiceAPI.sendInvoice(response.data.invoice._id);
        toast({
          title: 'Invoice Created & Sent',
          description: `Invoice ${response.data.invoice.invoice_number} has been sent to the contractor`,
        });
      } else {
        toast({
          title: 'Invoice Created',
          description: `Invoice ${response.data.invoice.invoice_number} saved as draft`,
        });
      }
      
      navigate('/invoices');
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create invoice',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8" data-testid="create-invoice-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/invoices')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-600" />
              Create Invoice
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Create a professional invoice for your work
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {/* Bill To Section */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                Bill To (Contractor) *
              </label>
              <select
                value={selectedContractor}
                onChange={(e) => setSelectedContractor(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="contractor-select"
              >
                <option value="">Select a contractor...</option>
                {contractors.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {contractors.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No contractors found. Apply to and get accepted for jobs first.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Briefcase className="h-4 w-4 inline mr-1" />
                Related Job (Optional)
              </label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                data-testid="job-select"
              >
                <option value="">No specific job</option>
                {jobs.filter(j => !selectedContractor || j.contractor_id === selectedContractor).map((j) => (
                  <option key={j.id} value={j.id}>{j.title}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <DollarSign className="h-4 w-4 inline mr-1" />
              Line Items *
            </label>
            <div className="space-y-3">
              {/* Header */}
              <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-2">
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2">Unit Price</div>
                <div className="col-span-2">Amount</div>
                <div className="col-span-1"></div>
              </div>
              
              {lineItems.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${index}`}>
                  <div className="col-span-12 md:col-span-5">
                    <Input
                      placeholder="Description of work..."
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        className="pl-7"
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                  </div>
                  <div className="col-span-3 md:col-span-2">
                    <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-right font-medium">
                      ${item.amount.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(index)}
                      disabled={lineItems.length === 1}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={addLineItem}
                className="w-full border-dashed"
                data-testid="add-line-item-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Line Item
              </Button>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-full md:w-80 space-y-2">
              <div className="flex justify-between py-2 border-t border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-600 dark:text-gray-400">Tax Rate (%)</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  className="w-24 text-right"
                  data-testid="tax-rate-input"
                />
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 dark:text-gray-400">Tax ({taxRate}%)</span>
                  <span className="font-medium">${calculateTax().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-t-2 border-gray-900 dark:border-white">
                <span className="text-lg font-bold text-gray-900 dark:text-white">Total</span>
                <span className="text-lg font-bold text-blue-600">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Due Date (Optional)
              </label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="due-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment terms, additional information..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                data-testid="notes-input"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
            <Button
              variant="outline"
              onClick={() => navigate('/invoices')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="flex-1"
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="send-invoice-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {loading ? 'Creating...' : 'Create & Send'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoice;
